import { inject, injectable } from '../../modules/common/di';
import { UsersAndLocationsApi } from '../api/admin/usersAndLocations/usersAndLocationsApi';
import { GetOrganizationUsersApiResponse } from '../api/admin/usersAndLocations/getOrganizationUsersApiResponse';
import { AdministratorsApi } from '../api/admin/administrators/administratorsApi';
import { SearchUsersApiResponse } from '../api/admin/administrators/searchUsersApiResponse';
import { UserService } from '@peoplepower/webcore/dist/types/data/services/userService';

@injectable('UserAdminService')
export class UserAdminService extends UserService {
  @inject('UsersAndLocationsApi') protected readonly usersAndLocationsApi!: UsersAndLocationsApi;
  @inject('AdministratorsApi') protected readonly administratorsApi!: AdministratorsApi;

  constructor() {
    super();
  }

  /**
   * Gets users by params. Administrative method.
   * @param [params] Request parameters.
   * @param {number} [params.organizationId] Organization ID to search within.
   * @param {number} [params.userId] Get a user by a specific user ID..
   * @param {string} [params.externalUserId] Search by external user ID.
   * @param {number} [params.locationId] Search for a user at a specific location ID.
   * @param {string} [params.searchBy] Searches for matching user login name, ID, email address, phone number, first name, last name. Use * for a wildcard.
   * @param {string} [params.searchAddress] Searches by location address fields: street, city, zip code.
   * @param {number} [params.servicePlanId] Filter users, who have specific service plan.
   * @param {string} [params.searchTag] Search by user tag
   * @param {number} [params.limit] The maximum number of user records to retrieve in this request.
   * @param {boolean} [params.getTags] Return user tags
   * @returns {Promise<Users>}
   */
  public getUsers(params?: {
    organizationId?: number;
    userId?: number;
    externalUserId?: string;
    locationId?: number;
    searchBy?: string;
    searchAddress?: string;
    servicePlanId?: number;
    searchTag?: string;
    limit?: number;
    getTags?: boolean;
  }): Promise<GetOrganizationUsersApiResponse> {
    return this.usersAndLocationsApi.getOrganizationUsers(params);
  }

  /**
   * Hidden API to search users across cloud.
   * @param {string|string[]} [email] Email(s) to search user by. Multiple allowed.
   * @param {number|number[]} [phone] Phone number(s) to search user by. Multiple allowed.
   * @returns {Promise<SearchUsersApiResponse>}
   */
  public searchUsers(email?: string | string[], phone?: number | number[] | string | string[]): Promise<SearchUsersApiResponse> {
    if (!email && !phone) {
      return this.reject('No email or phone provided.');
    }
    return this.administratorsApi.searchUsers({
      email: email || void 0,
      phone: phone || void 0,
    });
  }
}
